import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useProducts } from '@/hooks/useProducts';
import { type ProductInfo, type SetupQuoteResult } from '@/lib/sales-metrics';
import {
  pricingService,
  type ArnCrmValidationResult,
  type MfboxPricingSlabRow,
  type SetupQuotePayload,
} from '@/services/pricing.service';
import { QUERY_KEYS } from '@/utils/constants';
import { parseAdditionalArnTermYears } from '@/utils/productTermPricing';

const LEGACY_AUM_RATE_CARDS: Record<string, string> = {
  MFBOX_SILVER: 'silver',
  MFBOX_GOLD: 'gold_new',
};

/** @deprecated Build from catalog via buildAumProductMap */
export const AUM_BASED_CODES: Record<string, string> = LEGACY_AUM_RATE_CARDS;

export function buildAumProductMap(catalog: ProductInfo[] | undefined): Record<string, string> {
  const map: Record<string, string> = { ...LEGACY_AUM_RATE_CARDS };
  catalog?.forEach((product) => {
    if (product.isAumBased && product.rateCard) {
      map[product.code] = product.rateCard;
    }
  });
  return map;
}

export function useAumProductMap(): Record<string, string> {
  const { data: catalog } = useProducts();
  return useMemo(() => buildAumProductMap(catalog), [catalog]);
}

/** Picks the single AUM product code (Gold > Silver > first selected). */
export function getAumProductCode(
  selected: string[],
  aumMap: Record<string, string> = LEGACY_AUM_RATE_CARDS,
): string | null {
  const aumSelected = selected.filter((code) => aumMap[code]);
  if (aumSelected.length === 0) return null;
  if (aumSelected.includes('MFBOX_GOLD')) return 'MFBOX_GOLD';
  if (aumSelected.includes('MFBOX_SILVER')) return 'MFBOX_SILVER';
  return aumSelected[0] ?? null;
}

export function getAumPlanTier(
  selected: string[],
  aumMap: Record<string, string> = LEGACY_AUM_RATE_CARDS,
): string | null {
  const productCode = getAumProductCode(selected, aumMap);
  if (!productCode) return null;
  return aumMap[productCode] ?? null;
}

export function isAumProductCode(
  productCode: string,
  aumMap: Record<string, string> = LEGACY_AUM_RATE_CARDS,
): boolean {
  return Boolean(aumMap[productCode]);
}

export function useCalculateSetupQuote() {
  return useMutation<SetupQuoteResult, Error, SetupQuotePayload>({
    mutationFn: (payload) => pricingService.calculateSetup(payload),
  });
}

export function useValidateArnInCrm() {
  return useMutation<ArnCrmValidationResult, Error, string>({
    mutationFn: (arn) => pricingService.validateArnInCrm(arn),
  });
}

export function usePricingConfig() {
  return useQuery({
    queryKey: QUERY_KEYS.PRICING.CONFIG,
    queryFn: () => pricingService.getConfig(),
    staleTime: 60_000,
  });
}

export function useMfboxSlabs() {
  return useQuery<MfboxPricingSlabRow[]>({
    queryKey: QUERY_KEYS.PRICING.SLABS,
    queryFn: () => pricingService.getSlabs(),
    staleTime: 60_000,
  });
}

export function getCompleteAdditionalArns(
  rows: Array<{ arn: string; clientName: string; aum?: number; termYears?: number }>,
): Array<{ arn: string; clientName: string; aum: number; termYears: number }> {
  return rows
    .filter((row) => {
      const arn = row.arn.trim();
      const clientName = row.clientName.trim();
      const { aum } = row;
      return Boolean(arn && clientName && aum != null && Number.isFinite(aum) && aum >= 0);
    })
    .map((row) => ({
      arn: row.arn.trim(),
      clientName: row.clientName.trim(),
      aum: row.aum ?? 0,
      termYears: parseAdditionalArnTermYears(row.termYears),
    }));
}
